import { DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { ContextDocument } from '../models';
import { formatFileSize, getContextDocFileTypeLabel } from '../utils/document.utils';

/** Shape consumed by `<app-document-preview-modal>` — same vocabulary
 *  the `vendors/registry-detail/evidence-tab` preview state uses, so
 *  the modal stays agnostic to the kind of document it's showing. */
export interface ContextDocPreviewState {
  docId: string;
  fileName: string;
  mimeType: string | null;
  url: string | null;
  subtitle: string;
  loading: boolean;
}

export interface ContextDocPreviewHandler {
  /** Bound to the modal's `[isVisible]` / `[sourceUrl]` / etc. via
   *  `@if (state(); as p)`. `null` keeps the modal closed. */
  state: ReturnType<typeof signal<ContextDocPreviewState | null>>;
  /** Open the preview modal for a context document. The doc's
   *  presigned `download_url` (1 h TTL, minted by the BE list call)
   *  goes straight to the modal — no extra roundtrip. */
  open: (doc: ContextDocument) => void;
  /** Close + clear the preview state. */
  close: () => void;
  /** Re-mint the URL for the doc currently being previewed. The host's
   *  configured `refetch` returns a fresh document list (the BE
   *  re-issues 1 h presigned URLs on every call); the handler finds
   *  the same doc by id and re-emits the preview. If the doc has
   *  disappeared (deleted concurrently), the modal closes — its
   *  absence is itself the answer. */
  retry: () => void;
  /** Open the previewed doc in a new browser tab. The user's escape
   *  hatch when the iframe shows the browser's native PDF.js error
   *  (rendered inside the cross-origin iframe, not suppressible from
   *  outside). */
  openInNewTab: () => void;
}

interface ContextDocPreviewConfig {
  /** Component lifecycle scope — RxJS subscription auto-tears down. */
  destroyRef: DestroyRef;
  /** Re-fetch the doc list so fresh presigned URLs are minted. The
   *  handler picks the previewed doc out of the result by id. Return
   *  `null` (or an observable that errors) when there's no org context
   *  available — the handler will close the modal in that case. */
  refetch: (doc: ContextDocument | null) => Observable<ContextDocument[]> | null;
}

/** Single source of truth for "preview a context doc" state machine —
 *  shared by every surface that lets the user inspect a context doc
 *  (assessment / document drawers, edit-assessment, the main documents
 *  page + its context-doc drawer). New surfaces should consume this
 *  rather than re-implement the open/close/retry triple. */
export function createContextDocPreviewHandler(
  config: ContextDocPreviewConfig
): ContextDocPreviewHandler {
  const state = signal<ContextDocPreviewState | null>(null);
  let currentDoc: ContextDocument | null = null;
  let requestGeneration = 0;

  /** Shared "find the doc with this id in the refetched list, hydrate
   *  state with its fresh URL" routine. Used by both `open()` (when
   *  the caller's doc has no URL — e.g., the bulk-list endpoint
   *  omitted it) and `retry()` (when an expired URL needs re-minting).
   *  Keeping one path means consumers never see divergent behaviour
   *  between the two entry points. */
  function _resolveUrl(): void {
    const generation = ++requestGeneration;
    const requestedDocId = currentDoc?.context_document_id;
    const obs = config.refetch(currentDoc);
    if (!obs) {
      // No org context → drop to a "no URL" state. The modal renders
      // its "Couldn't load preview" branch which carries its own
      // recovery affordances; we don't close the modal silently.
      const cur = state();
      if (cur) state.set({ ...cur, loading: false });
      return;
    }
    obs.pipe(takeUntilDestroyed(config.destroyRef)).subscribe({
      next: docs => {
        if (generation !== requestGeneration || currentDoc?.context_document_id !== requestedDocId)
          return;
        const current = state();
        if (!current) return;
        const fresh = docs.find(d => d.context_document_id === current.docId);
        if (!fresh) {
          // The doc has disappeared (deleted concurrently). Its
          // absence is itself the answer; close so the user isn't
          // stuck looking at a stale preview shell.
          close();
          return;
        }
        state.set({ ...current, url: fresh.download_url ?? null, loading: false });
      },
      error: () => {
        if (generation !== requestGeneration || currentDoc?.context_document_id !== requestedDocId)
          return;
        const cur = state();
        if (cur) state.set({ ...cur, loading: false });
      },
    });
  }

  function open(doc: ContextDocument): void {
    // A pending URL lookup for the previous preview must not mutate this one.
    requestGeneration++;
    currentDoc = doc;
    const typeLabel = getContextDocFileTypeLabel(doc.file_type);
    const subtitle = doc.file_size ? `${typeLabel} · ${formatFileSize(doc.file_size)}` : typeLabel;
    const hasUrl = !!doc.download_url;
    state.set({
      docId: doc.context_document_id,
      fileName: doc.file_name,
      mimeType: doc.file_type || null,
      url: doc.download_url ?? null,
      subtitle,
      // The BE's bulk-list endpoint can omit `download_url` to keep
      // list responses cheap (presigning is per-object work). When
      // that's the case we kick off a doc_ids-filtered fetch which
      // mints a fresh 1 h URL — same path `retry()` uses. The modal
      // shows the "Fetching preview…" spinner during this round-trip.
      loading: !hasUrl,
    });
    if (!hasUrl) _resolveUrl();
  }

  function close(): void {
    requestGeneration++;
    currentDoc = null;
    state.set(null);
  }

  function retry(): void {
    const current = state();
    if (!current) return;
    state.set({ ...current, url: null, loading: true });
    _resolveUrl();
  }

  function openInNewTab(): void {
    const current = state();
    if (!current?.url) return;
    window.open(current.url, '_blank', 'noopener,noreferrer');
  }

  return { state, open, close, retry, openInNewTab };
}
